/**
 * SSE-подписка на события пользователя (`GET /api/v1/events/stream`, docs/contracts/backend-conventions.md).
 * EventSource не передаёт заголовки, поэтому поток открывается по одноразовому билету (TD-06): перед каждым
 * подключением — `POST /api/v1/events/ticket` (заголовки сессии и клиники), затем `…/events/stream?ticket=…&clinicId=…`.
 * Access-токен в строку запроса не попадает (журналы прокси, история браузера).
 * Переподключение: экспоненциальная задержка с разбросом, новый билет перед каждым подключением,
 * пауза без сети, сторожевой таймер пульса (ping) — зависшее соединение переоткрывается.
 */

/** Адрес потока по билету; клиника — та, для которой выдан билет (сервер сверяет). */
export function eventStreamTicketUrl(baseUrl: string, ticket: string, clinicId?: string | null): string {
  const url = new URL('/api/v1/events/stream', baseUrl);
  url.searchParams.set('ticket', ticket);
  if (clinicId) url.searchParams.set('clinicId', clinicId);
  return url.toString();
}

export type EventStreamState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'offline';

export interface RealtimeMessage<T = Record<string, unknown>> {
  type: string;
  id: string | null;
  data: T;
}

export type RealtimeHandler = (message: RealtimeMessage) => void;

export interface EventSourceLike {
  addEventListener(type: string, listener: (event: MessageEvent) => void): void;
  removeEventListener(type: string, listener: (event: MessageEvent) => void): void;
  close(): void;
  onopen: ((event: Event) => void) | null;
  onerror: ((event: Event) => void) | null;
}

export interface EventStreamOptions {
  baseUrl: string;
  /** Одноразовый билет потока (POST /api/v1/events/ticket); null — нет сессии, подключаться не нужно; ошибка — повтор с паузой. */
  getTicket: () => Promise<string | null>;
  getClinicId: () => string | null;
  createEventSource?: (url: string) => EventSourceLike;
  /** Нет пульса дольше — соединение считается зависшим (сервер шлёт ping раз в REALTIME_HEARTBEAT_SECONDS). */
  heartbeatTimeoutMs?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  random?: () => number;
}

const SERVICE_EVENTS = ['ready', 'ping'];

export class EventStreamClient {
  private source: EventSourceLike | null = null;
  private state: EventStreamState = 'idle';
  private attempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private generation = 0;
  private readonly handlers = new Map<string, Set<RealtimeHandler>>();
  private readonly attached = new Map<string, (event: MessageEvent) => void>();
  private readonly stateListeners = new Set<(state: EventStreamState) => void>();
  private readonly onOnline = () => {
    if (this.running && this.state === 'offline') void this.connect();
  };
  private readonly onOffline = () => {
    if (!this.running) return;
    this.closeSource();
    this.setState('offline');
  };

  constructor(private readonly options: EventStreamOptions) {}

  getState(): EventStreamState {
    return this.state;
  }

  onStateChange(listener: (state: EventStreamState) => void): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  /** Подписка на тип события (`scribe.note.ready`) или на все (`*`). */
  subscribe(type: string, handler: RealtimeHandler): () => void {
    const set = this.handlers.get(type) ?? new Set<RealtimeHandler>();
    set.add(handler);
    this.handlers.set(type, set);
    if (type !== '*') this.attach(type);
    return () => {
      set.delete(handler);
      if (set.size === 0) this.handlers.delete(type);
    };
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onOnline);
      window.addEventListener('offline', this.onOffline);
    }
    void this.connect();
  }

  /** Переподключиться (новая клиника или новый токен). */
  restart(): void {
    if (!this.running) return;
    this.attempts = 0;
    this.closeSource();
    void this.connect();
  }

  stop(): void {
    this.running = false;
    this.generation += 1;
    this.closeSource();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onOnline);
      window.removeEventListener('offline', this.onOffline);
    }
    this.setState('idle');
  }

  private setState(state: EventStreamState): void {
    if (this.state === state) return;
    this.state = state;
    for (const listener of this.stateListeners) listener(state);
  }

  private async connect(): Promise<void> {
    if (!this.running) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.setState('offline');
      return;
    }
    const generation = ++this.generation;
    this.setState(this.attempts === 0 ? 'connecting' : 'reconnecting');
    let ticket: string | null = null;
    try {
      ticket = await this.options.getTicket();
    } catch {
      if (generation === this.generation) this.scheduleReconnect();
      return;
    }
    if (generation !== this.generation || !this.running) return;
    if (!ticket) {
      this.setState('idle');
      return;
    }
    const url = eventStreamTicketUrl(this.options.baseUrl, ticket, this.options.getClinicId());
    const create = this.options.createEventSource ?? ((address: string) => new EventSource(address) as unknown as EventSourceLike);
    const source = create(url);
    this.source = source;
    this.attached.clear();
    source.onopen = () => {
      if (this.source !== source) return;
      this.attempts = 0;
      this.setState('open');
      this.armHeartbeat();
    };
    source.onerror = () => {
      if (this.source !== source) return;
      this.closeSource();
      this.scheduleReconnect();
    };
    for (const type of new Set([...SERVICE_EVENTS, ...this.handlers.keys()])) {
      if (type !== '*') this.attach(type);
    }
  }

  private attach(type: string): void {
    const source = this.source;
    if (!source || this.attached.has(type)) return;
    const listener = (event: MessageEvent) => this.dispatch(type, event);
    source.addEventListener(type, listener);
    this.attached.set(type, listener);
  }

  private dispatch(type: string, event: MessageEvent): void {
    this.armHeartbeat();
    if (SERVICE_EVENTS.includes(type) && !this.handlers.has(type)) return;
    let data: Record<string, unknown> = {};
    try {
      data = typeof event.data === 'string' && event.data !== '' ? (JSON.parse(event.data) as Record<string, unknown>) : {};
    } catch {
      data = {};
    }
    const message: RealtimeMessage = { type, id: event.lastEventId || null, data };
    for (const handler of this.handlers.get(type) ?? []) handler(message);
    if (!SERVICE_EVENTS.includes(type)) for (const handler of this.handlers.get('*') ?? []) handler(message);
  }

  private armHeartbeat(): void {
    if (this.heartbeatTimer) clearTimeout(this.heartbeatTimer);
    const timeout = this.options.heartbeatTimeoutMs ?? 75_000;
    this.heartbeatTimer = setTimeout(() => {
      if (!this.running || !this.source) return;
      this.closeSource();
      this.scheduleReconnect();
    }, timeout);
  }

  private scheduleReconnect(): void {
    if (!this.running) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.setState('offline');
      return;
    }
    const initial = this.options.initialDelayMs ?? 1000;
    const max = this.options.maxDelayMs ?? 30_000;
    const random = this.options.random ?? Math.random;
    const base = Math.min(max, initial * 2 ** this.attempts);
    const delay = Math.round(base * (0.5 + random() * 0.5));
    this.attempts += 1;
    this.setState('reconnecting');
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  private closeSource(): void {
    if (this.heartbeatTimer) clearTimeout(this.heartbeatTimer);
    this.heartbeatTimer = null;
    const source = this.source;
    this.source = null;
    if (!source) return;
    for (const [type, listener] of this.attached) source.removeEventListener(type, listener);
    this.attached.clear();
    source.onopen = null;
    source.onerror = null;
    source.close();
  }
}
